/**
 * Single reusable modal component for all forms.
 */

/**
 * ? Why is react dev tools throwing error in console when blockScrollOnMount is set to true
 * @see set blockScrollOnMount to true on the modal and check the console {@link https://github.com/chakra-ui/chakra-ui/issues/6213 }
 */

import { Modal, ModalOverlay, useBreakpointValue } from "@chakra-ui/react";
import { MemoExoticComponent, useCallback, useMemo } from "react";
import { ModalTypes } from "../../../config/types/modals";
import useAuth from "../../../hooks/useAuth";
import ForgotPassword from "../../forms/ForgotPassword";
import LoginForm from "../../forms/LoginForm";
import PrivacyPolicy from "../../forms/PrivacyPolicy";
import ResetPassword from "../../forms/ResetPassword";
import SavePhNumber from "../../forms/SavePhNumber";
import SignupForm from "../../forms/SignupForm";
import VerificationCode from "../../forms/VerificationCode";
import { deepFreeze } from "../../helpers/miscellaneous";
import UserProfile from "./UserProfile";

type TChangeModal = (modalType: ModalTypes) => void;
interface IHJSModalProps {
  /**
   * Extracted from useDisclosure() hook from chakra
   */
  isOpen: boolean;
  onClose: () => void;

  /**
   * Used for changing the type of the Modal
   */
  changeModalType: TChangeModal;

  /**
   * Determines the content of the modal based on the type of the Modal
   *
   * @defaultValue ModalTypes.LOGIN
   */
  modalType: ModalTypes;
}

type THJSModal = (props: IHJSModalProps) => JSX.Element;
type TModalComponent = ({
  changeModalType,
}: {
  changeModalType: TChangeModal;
}) => JSX.Element;
type TLoginAndLogoutComponent = ({
  changeModalType,
  onClose,
}: {
  changeModalType: TChangeModal;
  onClose: () => void;
}) => JSX.Element;
type TModalComponentMemoized =
  | MemoExoticComponent<TModalComponent>
  | TModalComponent
  | TLoginAndLogoutComponent;

const ModalComponents: {
  [X: string]: {
    size: string;
    component: TModalComponentMemoized;
  };
} = deepFreeze({
  [ModalTypes.LOGIN]: { size: "lg", component: LoginForm },
  [ModalTypes.SIGNUP]: { size: "xl", component: SignupForm },
  [ModalTypes.PRIVACY_POLICY]: { size: "xl", component: PrivacyPolicy },
  // [ModalTypes.LOGOUT]: { size: "lg", component: <></> },
  [ModalTypes.FORGOT_PASSWORD]: { size: "lg", component: ForgotPassword },
  [ModalTypes.CONFIRM_FORGOT_PASSWORD]: {
    size: "lg",
    component: ResetPassword,
  },
  [ModalTypes.VERIFICATION_CODE]: {
    size: "lg",
    component: VerificationCode,
  },
  [ModalTypes.USER_PROFILE]: {
    size: "md",
    component: UserProfile,
  },
  [ModalTypes.PHONE_NUMBER]: {
    size: "lg",
    component: SavePhNumber,
  },
});

/**
 * @remarks
 * Desktop:
 * Sign Up and Privacy Policy Modals' size is xl
 * Login, forgot password, verification and reset password Modals' size is lg
 */

const HJSModal: THJSModal = ({
  isOpen,
  onClose,
  modalType = ModalTypes.LOGIN,
  changeModalType,
}: IHJSModalProps) => {
  const { isAuthenticated } = useAuth();
  const CloseModalHandler = useCallback(() => {
    if (isAuthenticated) {
      changeModalType(ModalTypes.USER_PROFILE);
    } else {
      changeModalType(ModalTypes.LOGIN);
    }
    onClose();
  }, [changeModalType, isAuthenticated, onClose]);

  const { component: ModalTypeComponent } = ModalComponents[modalType];

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const size = useMemo(() => (isMobile === true ? "xs" : "xl"), [isMobile]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={CloseModalHandler}
      allowPinchZoom
      blockScrollOnMount={false}
      closeOnEsc
      closeOnOverlayClick
      isCentered
      size={size}
    >
      <ModalOverlay />
      (
      <ModalTypeComponent changeModalType={changeModalType} onClose={onClose} />
      )
    </Modal>
  );
};

export default HJSModal;
